import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '@/assets/image/THE_MOON.jpg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [selectedLang, setSelectedLang] = useState(
    localStorage.getItem('i18nextLng') || 'vi'
  );

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setSelectedLang(code);
    localStorage.setItem('i18nextLng', code);
    setLangOpen(false);
  };

  useEffect(() => {
    setSelectedLang(i18n.language);
  }, [i18n.language]);

  const navigation = t('navigation', { returnObjects: true }) as {
    name: string;
    href: string;
  }[];

  const languages = [
    { code: 'vi', label: 'Tiếng Việt' },
    { code: 'en', label: 'English' },
    { code: 'zh', label: '中文' },
  ];

  const isActive = (path: string) => location.pathname === path;
  return (
    <header className="bg-primary-dark text-white shadow-md sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <div className="w-10 h-10 bg-accent-gold rounded-full flex items-center justify-center shadow-sm overflow-hidden">
              <img
                src={Logo}
                alt="Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="leading-tight">
              <span
                className="text-xl font-bold tracking-wide"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                The Moon Hotel
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-4">
            <nav className="flex items-center space-x-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-accent-gold text-white shadow-md'
                      : 'text-white hover:bg-accent-gold/80 hover:shadow-md'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-2 px-3 py-1.5 border border-white/30 rounded-lg hover:bg-white/10 transition-all"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm">
                  {languages.find((l) => l.code === selectedLang)?.label}
                </span>
                <i className="ri-arrow-down-s-line text-lg"></i>
              </button>

              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white text-black rounded-lg shadow-lg overflow-hidden animate-fadeIn z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 transition"
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="tel:0933537986"
              className="flex items-center space-x-2 bg-accent-gold text-white px-4 py-2 rounded-lg font-medium hover:bg-[#8C6D45] shadow-md transition-all duration-200"
            >
              <i className="ri-phone-line text-lg"></i>
              <span>0933 537 986</span>
            </a>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-2 py-1 border border-white/30 rounded-lg hover:bg-white/10 transition"
              >
                <i className="ri-global-line text-lg"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white text-black rounded-lg shadow-lg overflow-hidden animate-fadeIn z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 transition"
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="tel:0933537986"
              className="flex items-center px-3 py-1.5 bg-accent-gold rounded-lg hover:bg-[#8C6D45] transition"
            >
              <i className="ri-phone-line text-lg"></i>
            </a>

            {/* Menu */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <i
                className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {isMenuOpen && (
          <div className="lg:hidden py-3 border-t border-white/10 animate-fadeIn">
            <nav className="space-y-2 px-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive(item.href)
                      ? 'bg-accent-gold text-white shadow-md'
                      : 'text-white hover:bg-accent-gold/90'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
